import { Draft } from 'immer';

import { toSuccess } from '../../resolvers/definition/resolverState';
import { produceNext } from '../../utils/immer';
import { Link, LinkContextPair } from './link';

const noContexts = (): LinkContextPair[] => [];

export const pushFromData =
  <TSelfParams, TSelfData, TOtherParams, TOtherData>(
    transform: (opts: {
      self: { data: TSelfData; params: TSelfParams; context?: unknown };
      other: {
        data?: Draft<TOtherData>;
        params: TOtherParams;
        context?: unknown;
      };
      getContexts: () => LinkContextPair[];
    }) => TOtherData | void
  ): Link<TSelfParams, TSelfData, TOtherParams, TOtherData>['onPush'] =>
  ({ dataChanged, self, other, getContexts }) => {
    if (self.state?.data === undefined || !dataChanged) {
      return;
    }

    const selfData = self.state.data;

    const data = produceNext(other.state?.data, (draft) =>
      transform({
        self: {
          data: selfData,
          params: self.params,
          context: self.context,
        },
        other: {
          data: draft,
          params: other.params,
          context: other.context,
        },
        getContexts: getContexts ?? noContexts,
      })
    );

    if (data !== undefined) {
      return toSuccess(data)();
    }
  };

export const pushFromDataImmutable =
  <TSelfParams, TSelfData, TOtherParams, TOtherData>(
    transform: (opts: {
      self: { data: TSelfData; params: TSelfParams; context?: unknown };
      other: { data?: TOtherData; params: TOtherParams; context?: unknown };
      getContexts: () => LinkContextPair[];
    }) => TOtherData | void
  ): Link<TSelfParams, TSelfData, TOtherParams, TOtherData>['onPush'] =>
  ({ dataChanged, self, other, getContexts }) => {
    if (self.state?.data === undefined || !dataChanged) {
      return;
    }

    const data = transform({
      self: {
        data: self.state.data,
        params: self.params,
        context: self.context,
      },
      other: {
        data: other.state?.data,
        params: other.params,
        context: other.context,
      },
      getContexts: getContexts ?? noContexts,
    });

    if (data !== undefined) {
      return toSuccess(data)();
    }
  };

export const pullFromData =
  <TSelfParams, TSelfData, TOtherParams, TOtherData>(
    transform: (opts: {
      self: {
        data?: Draft<TSelfData>;
        params: TSelfParams;
        context?: unknown;
      };
      other: { data: TOtherData; params: TOtherParams; context?: unknown };
      getContexts: () => LinkContextPair[];
    }) => TSelfData | void
  ): Link<TSelfParams, TSelfData, TOtherParams, TOtherData>['onPull'] =>
  ({ dataChanged, self, other, getContexts }) => {
    if (other.state?.data === undefined || !dataChanged) {
      return;
    }

    const otherData = other.state.data;

    const data = produceNext(self.state?.data, (draft) =>
      transform({
        self: {
          data: draft,
          params: self.params,
          context: self.context,
        },
        other: {
          data: otherData,
          params: other.params,
          context: other.context,
        },
        getContexts: getContexts ?? noContexts,
      })
    );

    if (data !== undefined) {
      return toSuccess(data)();
    }
  };

export const pullFromDataImmutable =
  <TSelfParams, TSelfData, TOtherParams, TOtherData>(
    transform: (opts: {
      self: { data?: TSelfData; params: TSelfParams; context?: unknown };
      other: { data: TOtherData; params: TOtherParams; context?: unknown };
      getContexts: () => LinkContextPair[];
    }) => TSelfData | void
  ): Link<TSelfParams, TSelfData, TOtherParams, TOtherData>['onPull'] =>
  ({ dataChanged, self, other, getContexts }) => {
    if (other.state?.data === undefined || !dataChanged) {
      return;
    }

    const data = transform({
      self: {
        data: self.state?.data,
        params: self.params,
        context: self.context,
      },
      other: {
        data: other.state.data,
        params: other.params,
        context: other.context,
      },
      getContexts: getContexts ?? noContexts,
    });

    if (data !== undefined) {
      return toSuccess(data)();
    }
  };
